/**
 * Головний дашборд для ролі CTO (volodp): портфель СЕС плитками 3×2 за
 * дизайн-концепцією Прогноз/EMS Box (токени, Urbanist, Lucide, скляні плитки,
 * догма, світло/темна). Дані: GET /scada/overview (весь флот) + погода Open-Meteo.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Zap, Sun, Building2, Gauge } from 'lucide-react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { getStoredUser } from '@/hooks/useAuth';
import { API_BASE_URL, authFetch } from '@/services/api';
import type { OverviewResponse, StationLive, StationWeather } from './overviewTypes';
import { fmtMw, fmtMwh, fmtPct, loadPct, fetchPortfolioWeather } from './overviewTypes';
import StationTile from './StationTile';
import StationMap from './StationMap';
import WindyPortfolioMap from './WindyPortfolioMap';
import RdnPriceChips from './RdnPriceChips';
import RdnIndexBlock from './RdnIndexBlock';
import PeriodOverviewBlock from './PeriodOverviewBlock';
import PlannedIncomeBlock from '@/pages/financial/result/PlannedIncomeBlock';
import ImbalanceBlock from '@/pages/financial/result/ImbalanceBlock';
import { hasArtemKView } from '@/utils/artemkView';

/**
 * ПОЛОЖЕННЯ РЯДКА НАВІГАЦІЇ ПЕРІОДУ ВІДНОСНО ПРИВІТАННЯ (px) — для ArtemK.
 * Єдине місце, де його регулюють: змінюєш число й перезбираєш.
 *
 * Це нижній відступ шапки привітання; від'ємний тягне рядок навігації вгору.
 *   · на lg+ (NAV_GAP_LG) рядок піднято НА РІВЕНЬ дати «Понеділок, 5 жовтня»:
 *     заголовок «Доброго дня, Артем» не рухається, навігація стоїть по центру в
 *     тому ж рядку, що й дата. -26 = висота дати (≈19.5) + її відступ (2) + ще
 *     ≈4.5, щоб 32px навігації центрувались по 20px дати. Більше за модулем —
 *     вище, менше — нижче;
 *   · нижче lg (NAV_GAP_SM) ширини на поруч не вистачає, тож навігація йде
 *     під привітанням зі звичайним відступом.
 * Відступ навігація → картки (12px) від цих чисел не залежить.
 */
const NAV_GAP_SM = 12;
const NAV_GAP_LG = -26;

function KpiChip({ label, value, unit, Icon }: { label: string; value: string; unit?: string; Icon: any }) {
  return (
    <div className="tile px-3.5 py-2.5 flex items-center gap-3">
      <div className="w-9 h-9 rounded-tile-sm flex items-center justify-center shrink-0" style={{ background: 'var(--surface-2)' }}>
        <Icon size={17} strokeWidth={1.75} style={{ color: 'var(--text-muted)' }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10.5px] truncate" style={{ color: 'var(--text-muted)' }}>{label}</div>
        {/* 26px — як у картках блоку періоду нижче: обидва ряди стоять на
            одному екрані, і різний кегль читався як різна важливість,
            хоча це просто два види зведення. */}
        <div className="flex items-baseline gap-1">
          <span className="font-display text-[26px] font-normal leading-none" style={{ color: 'var(--text)' }}>{value}</span>
          {unit && <span className="text-[10.5px]" style={{ color: 'var(--text-dim)' }}>{unit}</span>}
        </div>
      </div>
    </div>
  );
}

export default function PortfolioDashboard() {
  useTranslation();
  const [ov, setOv] = useState<OverviewResponse | null>(null);
  const [weather, setWeather] = useState<Record<string, StationWeather>>({});
  const [loading, setLoading] = useState(true);
  const wxKeyRef = useRef<string>('');

  // Живий годинник для шапки-привітання (оновлення щосекунди).
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const firstName = (getStoredUser()?.displayName || '').split(' ')[0] || i18n.t('ak_home_colleague');
  // Цей дашборд спільний для кількох акаунтів (CTO, tabaki, OksanaO та ін.),
  // тож зміни для ArtemK мусять бути під явною умовою: у нього розділ
  // зветься «Головна» (у решти — «Головна / Дашборд»). Блок новин, що стояв
  // тут під основним контентом, прибрано на прохання замовника.
  const isArtemK = hasArtemKView(getStoredUser()?.login);
  const h = now.getHours();
  const greet = h < 5 ? i18n.t('ak_home_good_night') : h < 12 ? i18n.t('ak_home_good_morning') : h < 18 ? i18n.t('ak_home_good_afternoon') : i18n.t('ak_home_good_evening');
  const dateStr = now.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { weekday: 'long', day: 'numeric', month: 'long' }).replace(/^./, (c) => c.toUpperCase());
  const timeStr = now.toLocaleTimeString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const load = useCallback(() => {
    authFetch(`${API_BASE_URL}/scada/overview`)
      .then((r) => (r.ok ? r.json() : null))
      .then((p: OverviewResponse | null) => { if (p) setOv(p); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // Погода: перезапит коли змінився набір станцій, і раз на 15 хв.
  useEffect(() => {
    const stations = ov?.stations ?? [];
    if (!stations.length) return;
    const key = stations.map((s) => s.assetId).join(',');
    const refresh = () => fetchPortfolioWeather(stations).then(setWeather);
    if (key !== wxKeyRef.current) { wxKeyRef.current = key; refresh(); }
    const t = setInterval(refresh, 900000);
    return () => clearInterval(t);
  }, [ov]);

  const stations: StationLive[] = ov?.stations ?? [];
  const single = stations.length === 1 ? stations[0] : null;

  // Одноактивний вигляд (акаунт Табаки): Плановий дохід + Небаланси за сьогодні.
  const [fin, setFin] = useState<any | null>(null);
  const loadFin = useCallback((assetId: string) => {
    const d = new Date();
    const iso = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    authFetch(`${API_BASE_URL}/financial/result/overview?assetId=${encodeURIComponent(assetId)}&period=day&date=${iso}`)
      .then((r) => (r.ok ? r.json() : null)).then(setFin).catch(() => setFin(null));
  }, []);
  useEffect(() => { if (single) loadFin(single.assetId); else setFin(null); }, [single?.assetId, loadFin]);
  const finGran = fin?.gran ?? 'hour';

  const kpis = useMemo(() => {
    const totalPac = stations.reduce((a, s) => a + (s.pacKw ?? 0), 0);
    const totalDaily = stations.reduce((a, s) => a + (s.dailyKwh ?? 0), 0);
    const active = stations.filter((s) => s.operationalStatus === 'active').length;
    const loads = stations.map(loadPct).filter((v): v is number => v != null);
    const avgLoad = loads.length ? loads.reduce((a, b) => a + b, 0) / loads.length : null;
    return { totalPac, totalDaily, active, total: stations.length, avgLoad };
  }, [stations]);

  return (
    <DashboardLayout breadcrumb={isArtemK ? [i18n.t('ak_home_home')] : [i18n.t('ak_home_home'), i18n.t('ak_home_dashboard')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: "Urbanist, Inter, sans-serif" }}>

          {/* Шапка + топ-KPI */}
          <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 ${isArtemK ? "mb-[var(--nav-gap-sm)] lg:mb-[var(--nav-gap-lg)]" : "mb-5"}`}
            style={isArtemK ? ({ "--nav-gap-sm": `${NAV_GAP_SM}px`, "--nav-gap-lg": `${NAV_GAP_LG}px` } as React.CSSProperties) : undefined}>
            <div>
              <h1 className="font-display text-2xl font-semibold leading-tight" style={{ color: 'var(--text)' }}>{greet}, {firstName}</h1>
              <p className="text-[13px] mt-0.5 flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                <span>{dateStr}</span>
                <span style={{ color: 'var(--line-strong)' }}>·</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>{timeStr}</span>
              </p>
            </div>
            {/* У ArtemK верхніх чіпів немає зовсім (рішення користувача 05.10):
                «Станцій активно» і «Сер. завантаження» прибрані як і попередні
                два — «Сумарна потужність» (стала величина) і «Вироблено
                сьогодні» (дублює «Генерацію факт» із ряду показників нижче).
                Шапка в нього лишається тільки з привітанням. */}
            {!isArtemK && (
              <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">
                <KpiChip label={i18n.t('ak_home_total_power')} value={fmtMw(kpis.totalPac)} unit={i18n.t('ak_home_mw')} Icon={Zap} />
                <KpiChip label={i18n.t('ak_home_generated_today_2')} value={fmtMwh(kpis.totalDaily)} unit={i18n.t('ak_home_mwh')} Icon={Sun} />
                <KpiChip label={i18n.t('ak_home_stations_active')} value={`${kpis.active}/${kpis.total}`} Icon={Building2} />
                <KpiChip label={i18n.t('ak_home_avg_load')} value={fmtPct(kpis.avgLoad)} unit="%" Icon={Gauge} />
              </div>
            )}
          </div>

          {loading && !stations.length && (
            <div className="tile p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>{i18n.t('ak_home_loading_portfolio')}</div>
          )}

          {/* ПОРЯДОК ЗА РЕФЕРЕНСОМ. Координати зі слайда презентації (у дюймах
              від верху): показники 0.87 → мапа 3.92 → плитки станцій 6.55 →
              ціни РДН 11.36 → динаміка доходу 13.80. Тобто мапа йде ПІД
              показниками на всю ширину, а не поруч із цінами, як було.

              Мапа, станції й ціни передаються в PeriodOverviewBlock дітьми:
              графік доходу має лишитись останнім, а розривати компонент на
              два не можна — вони ділять одну дату й один запит. */}
          {isArtemK ? (
            <PeriodOverviewBlock>
              {/* Висота ЧИСЛОМ, не «100%». У сітці станцій мапа отримувала
                  визначену висоту від рядка (gridAutoRows: 248), тут такого
                  рядка немає: обгортка має висоту, але сама плитка всередині
                  лишається авто, і height:100% на контейнері Windy резолвився
                  в нуль — мапа зникала. */}
              {stations.length > 0 && (
                <WindyPortfolioMap stations={stations} height={280} />
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" style={{ gridAutoRows: '248px' }}>
                {stations.map((s) => (
                  <StationTile key={s.assetId} s={s} weather={weather[s.assetId]} />
                ))}
              </div>

              {stations.length > 0 && (
                <>
                  {/* Блок індексів із навігацією за зразком oree.com.ua:
                      перемикач доба/місяць, датапікер із межами з даних,
                      картки зі стрілкою й відсотком зміни. Замінив
                      RdnPriceChips (сітка 2×3 без вибору дати) — той лишається
                      для решти акаунтів. */}
                  <RdnIndexBlock />
                </>
              )}
            </PeriodOverviewBlock>
          ) : single ? (
            /* Одноактивний вигляд (Табаки): фіксована сітка плиток (висота рядів 248).
               Ряд 1: [СЕС Табаки] [Мапа 2× за шириною].
               Ряд 2: [Плановий дохід] [Небаланси] [KPI-картка 2×2]. */
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" style={{ gridAutoRows: '248px' }}>
              <StationTile s={single} weather={weather[single.assetId]} />
              {single.latitude != null && single.longitude != null && (
                <StationMap lat={single.latitude} lon={single.longitude} name={single.name}
                  height="100%" className="xl:col-span-2" />
              )}
              {fin?.plannedIncome && <PlannedIncomeBlock data={fin.plannedIncome} gran={finGran} chartOnly />}
              {fin?.imbalance && (
                <ImbalanceBlock data={fin.imbalance} gran={finGran} period={fin.period}
                  monthClose={fin.monthClose} assetId={single.assetId} canEdit={false}
                  onChanged={() => loadFin(single.assetId)} chartOnly />
              )}
              {fin?.imbalance && (
                <ImbalanceBlock data={fin.imbalance} gran={finGran} period={fin.period}
                  monthClose={fin.monthClose} assetId={single.assetId} canEdit={false}
                  onChanged={() => loadFin(single.assetId)} kpiOnly dayDate={fin.range?.from} />
              )}
            </div>
          ) : (
            /* Портфель: ряд 1 — мапа (2 плитки) + чіпи РДН (1 плитка, 2×3);
               далі — сітка плиток станцій. Висота рядів фіксована (248). */
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" style={{ gridAutoRows: '248px' }}>
              {stations.length > 0 && (
                <WindyPortfolioMap stations={stations} height="100%" className="md:col-span-2" />
              )}
              {stations.length > 0 && <RdnPriceChips />}
              {stations.map((s) => (
                <StationTile key={s.assetId} s={s} weather={weather[s.assetId]} />
              ))}
            </div>
          )}

          {/* Блок новин прибрано з «Головної» на прохання замовника.
              Сторінка новин лишається робочою за адресою /info; сам компонент
              InfoRedesignContent і прапорець `embedded` нікуди не ділись, тож
              повернути блок — це один рядок.

              УВАГА: у меню ArtemK розділу новин немає, тож для нього /info
              тепер недосяжний з інтерфейсу. Якщо новини йому потрібні —
              треба повернути пункт у меню. */}
        </div>
      </div>
    </DashboardLayout>
  );
}
